import { zodResolver } from '@hookform/resolvers/zod';
import { NavigateNext } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useCreateReservationChatMutation } from '../../../../app/Slices/SevenDayApiSlice';
import { ReservationChat } from '../../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';

const Schema = z.object({
    message: z
        .string()
        .min(1, { message: 'Required' })
        .max(5000, { message: 'Message cannot exceed 5000 characters' }),
});

export default function SendPrivateMessage({ booking }: { booking: any }) {
    const loggedInUser = UserRole();
    const [messageSending, setMessageSending] = useState<boolean>(false);
    const [navigating, setNavigating] = useState<boolean>(false);
    const [createChat] = useCreateReservationChatMutation();

    type ContactForm = z.infer<typeof Schema>;

    const methods = useForm<ContactForm>({
        resolver: zodResolver(Schema),
        defaultValues: { message: '' },
    });

    const { control, handleSubmit } = methods;

    const onSubmit = async (data: ContactForm) => {
        try {
            setMessageSending(true);

            const body: ReservationChat = {
                bookingId: booking?.id,
                message: data.message,
                isPrivate: true,
            };

            const response = await createChat(body).unwrap();

            window.location.href = `/messages/${response.channelId}`;
        } catch {
            toast.error(
                `Error sending message to ${
                    loggedInUser?.role.includes('owner') ? 'guest' : 'host'
                }`,
            );
        } finally {
            setMessageSending(false);
        }
    };

    const existingPrivateMessage = booking?.privateMessages?.find(
        (message: any) => message?.userId === loggedInUser?.sub,
    );
    const onNavigate = async () => {
        setNavigating(true);
        window.location.href = `/messages/${existingPrivateMessage.channelId}`;
    };

    return (
        <Box sx={{ width: '100%' }}>
            {!existingPrivateMessage && (
                <Accordion sx={{ borderRadius: '7px' }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls='panel1-content'
                        id='panel1-header'
                    >
                        <Typography component='span'>
                            Message host privately
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormProvider {...methods}>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Box width='100%'>
                                    <>
                                        <FormTextField
                                            name='message'
                                            control={control}
                                            rows={3}
                                            multiline
                                        />
                                        <Box pt={1}>
                                            <CustomButton
                                                loading={messageSending}
                                                disabled={messageSending}
                                                variant='outlined'
                                                color='primary'
                                                type='submit'
                                            >
                                                Send message
                                            </CustomButton>
                                        </Box>
                                    </>
                                </Box>
                            </form>
                        </FormProvider>
                    </AccordionDetails>
                </Accordion>
            )}

            {existingPrivateMessage && (
                <>
                    <Typography
                        variant='body3'
                        sx={{
                            color: 'text.primary',
                        }}
                        gutterBottom
                    >
                        You have an existing private chat with the host. Click
                        below to revisit your past conversation!
                    </Typography>

                    <CustomButton
                        loading={navigating}
                        disabled={navigating}
                        variant='outlined'
                        color='primary'
                        onClick={onNavigate}
                        endIcon={<NavigateNext />}
                    >
                        Go to private chat
                    </CustomButton>
                </>
            )}
        </Box>
    );
}
